import { z } from 'zod';
import { 
 
  Project, 
  Task, 
  Proposal, 
  Contract,
  Asset,
  License,
  LeaveRequest
} from '@/types/models';

// User Validation
export const userSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  role: z.enum(['admin', 'manager', 'employee']),
  department: z.string().min(1, 'Department is required'),
  position: z.string().min(1, 'Position is required'),
  avatar: z.string().optional(),
  permissions: z.array(z.string())
});

// Employee Validation
export const employeeSchema = userSchema.extend({
  employeeId: z.string().min(1, 'Employee ID is required'),
  joiningDate: z.date(),
  salary: z.number().positive('Salary must be positive'),
  managerId: z.string().min(1, 'Manager ID is required'),
  status: z.enum(['active', 'on_leave', 'terminated']),
  documents: z.array(z.object({
    id: z.string(),
    type: z.enum(['id_proof', 'resume', 'certificate', 'other']),
    name: z.string(),
    url: z.string().url('Invalid URL'),
    uploadedAt: z.date()
  })),

});

// Project Validation
export const projectSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Project name is required'),
  description: z.string().min(1, 'Description is required'),
  clientId: z.string().min(1, 'Client is required'),
  managerId: z.string().min(1, 'Project manager is required'),
  startDate: z.date(),
  endDate: z.date().refine((date) => date > new Date(), {
    message: 'End date must be in the future',
  }),
  status: z.enum(['planning', 'active', 'on-hold', 'completed', 'cancelled']),
  budget: z.number().min(0, 'Budget must be a positive number'),
  team: z.array(z.object({
    userId: z.string(),
    role: z.string(),
  })),
  tasks: z.array(z.string()),
  documents: z.array(z.string()),
});

export type ProjectFormData = z.infer<typeof projectSchema>;

// Task Validation
export const taskSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  priority: z.enum(['High', 'Medium', 'Low']),
  status: z.enum(['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled']),
  assigneeId: z.string().min(1, 'Assignee is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  dependencies: z.array(z.string()).optional(),
  comments: z.array(z.object({
    id: z.string(),
    userId: z.string(),
    content: z.string(),
    createdAt: z.string(),
    attachments: z.array(z.object({
      id: z.string(),
      name: z.string(),
      url: z.string(),
      type: z.string(),
      size: z.number()
    })).optional()
  })).optional(),
  attachments: z.array(z.object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    type: z.string(),
    size: z.number()
  })).optional(),
  createdAt: z.string(),
  updatedAt: z.string()
});

// Client Validation
export const clientSchema = z.object({
  name: z.string().min(1, 'Client name is required'),
  type: z.enum(['individual', 'company']),
  industry: z.string().min(1, 'Industry is required'),
  status: z.enum(['active', 'inactive', 'lead']),
  contacts: z.array(z.object({
    name: z.string().min(1, 'Contact name is required'),
    position: z.string().min(1, 'Position is required'),
    email: z.string().email('Invalid email'),
    phone: z.string().min(10, 'Invalid phone number'),
    isPrimary: z.boolean()
  })),
  billingInfo: z.object({
    address: z.string().min(1, 'Address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    country: z.string().min(1, 'Country is required'),
    postalCode: z.string().min(1, 'Postal code is required'),
    taxId: z.string().optional(),
    paymentTerms: z.string().min(1, 'Payment terms are required')
  })
});

// Proposal Validation
export const proposalSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  title: z.string().min(1, 'Proposal title is required'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  value: z.number().positive('Value must be positive'),
  status: z.enum(['draft', 'sent', 'accepted', 'rejected']),
  validUntil: z.date().refine((date) => date > new Date(), {
    message: 'Valid until date must be in the future'
  }),
  items: z.array(z.object({
    description: z.string().min(1, 'Item description is required'),
    quantity: z.number().positive('Quantity must be positive'),
    unitPrice: z.number().positive('Unit price must be positive'),
    total: z.number().positive('Total must be positive')
  }))
});

// Contract Validation
export const contractSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  proposalId: z.string().min(1, 'Proposal is required'),
  type: z.enum(['fixed_price', 'recurring', 'subscription', 'time_materials']),
  startDate: z.date(),
  endDate: z.date().refine((date) => date > new Date(), {
    message: 'End date must be in the future'
  }),
  value: z.number().positive('Value must be positive'),
  status: z.enum(['active', 'expired', 'terminated']),
  terms: z.string().min(10, 'Terms must be at least 10 characters'),
  documents: z.array(z.string())
});

// Asset Validation
export const assetSchema = z.object({
  name: z.string().min(1, 'Asset name is required'),
  type: z.enum(['hardware', 'software', 'furniture', 'other']),
  category: z.string().min(1, 'Category is required'),
  status: z.enum(['active', 'maintenance', 'retired']),
  assignedTo: z.string().optional(),
  purchaseDate: z.date(),
  purchasePrice: z.number().positive('Purchase price must be positive'),
  warrantyExpiry: z.date().optional(),
  location: z.string().min(1, 'Location is required'),
  specifications: z.record(z.any())
});

// License Validation
export const licenseSchema = z.object({
  name: z.string().min(1, 'License name is required'),
  type: z.enum(['software', 'subscription', 'service']),
  vendor: z.string().min(1, 'Vendor is required'),
  startDate: z.date(),
  endDate: z.date().refine((date) => date > new Date(), {
    message: 'End date must be in the future'
  }),
  cost: z.number().positive('Cost must be positive'),
  status: z.enum(['active', 'expired', 'cancelled']),
  seats: z.number().positive('Number of seats must be positive'),
  usedSeats: z.number().min(0, 'Used seats cannot be negative'),
  assignedTo: z.array(z.string()),
  renewalDate: z.date()
});

// Invoice Validation
export const invoiceSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  contractId: z.string().min(1, 'Contract is required'),
  number: z.string().min(1, 'Invoice number is required'),
  issueDate: z.date(),
  dueDate: z.date().refine((date) => date > new Date(), {
    message: 'Due date must be in the future'
  }),
  amount: z.number().positive('Amount must be positive'),
  status: z.enum(['draft', 'sent', 'paid', 'overdue']),
  items: z.array(z.object({
    description: z.string().min(1, 'Item description is required'),
    quantity: z.number().positive('Quantity must be positive'),
    unitPrice: z.number().positive('Unit price must be positive'),
    total: z.number().positive('Total must be positive')
  })),
  notes: z.string().optional()
});

// Leave Request Validation
export const leaveRequestSchema = z.object({
  employeeId: z.string().min(1, 'Employee is required'),
  type: z.enum(['annual', 'sick', 'maternity', 'unpaid']),
  startDate: z.date(),
  endDate: z.date().refine((date) => date > new Date(), {
    message: 'End date must be in the future'
  }),
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
  documents: z.array(z.string()).optional()
}).refine((data) => data.endDate > data.startDate, {
  message: 'End date must be after start date'
});

// Business Logic Validation Functions
export const validateProjectTeam = (team: Project['team']) => {
  const totalAllocation = team.reduce((sum, member) => sum + member.allocation, 0);
  if (totalAllocation > 100) {
    throw new Error('Total team allocation cannot exceed 100%');
  }
};

export const validateTaskDependencies = (tasks: Task[], taskId: string) => {
  const task = tasks.find(t => t.id === taskId);
  if (!task) return;

  const visited = new Set<string>();
  const checkCycle = (currentId: string): boolean => {
    if (visited.has(currentId)) return true;
    visited.add(currentId);
    
    const currentTask = tasks.find(t => t.id === currentId);
    if (!currentTask) return false;

    return currentTask.dependencies.some(depId => checkCycle(depId));
  };

  if (checkCycle(taskId)) {
    throw new Error('Circular dependency detected in task dependencies');
  }
};

export const validateLeaveBalance = (
  leaveRequests: LeaveRequest[],
  employeeId: string,
  type: LeaveRequest['type'],
  startDate: Date,
  endDate: Date
) => {
  const existingLeaves = leaveRequests.filter(
    req => req.employeeId === employeeId &&
    req.status === 'approved' &&
    req.type === type &&
    ((req.startDate <= endDate && req.endDate >= startDate))
  );

  if (existingLeaves.length > 0) {
    throw new Error('Leave request overlaps with existing approved leaves');
  }
};

export const validateContractValue = (
  contract: Contract,
  proposal: Proposal
) => {
  if (contract.value !== proposal.value) {
    throw new Error('Contract value must match proposal value');
  }
};

export const validateAssetAssignment = (
  asset: Asset,
  employeeId: string
) => {
  if (asset.status !== 'active') {
    throw new Error('Cannot assign inactive asset');
  }
  if (asset.assignedTo && asset.assignedTo !== employeeId) {
    throw new Error('Asset is already assigned to another employee');
  }
};

export const validateLicenseSeats = (
  license: License,
  employeeId: string
) => {
  if (license.usedSeats >= license.seats) {
    throw new Error('No available seats in license');
  }
  if (license.assignedTo.includes(employeeId)) {
    throw new Error('Employee already has this license assigned');
  }
};

// Family Information Validation
export const validateAadhaarNumber = (aadhaar: string): boolean => {
  // Remove any spaces or special characters
  const cleanAadhaar = aadhaar.replace(/\D/g, '');
  // Check if it's exactly 12 digits
  return /^\d{12}$/.test(cleanAadhaar);
};

export const validateMobileNumber = (mobile: string): boolean => {
  // Remove any spaces or special characters
  const cleanMobile = mobile.replace(/\D/g, '');
  // Check if it's exactly 10 digits
  return /^\d{10}$/.test(cleanMobile);
};

export const formatMobileNumber = (mobile: string): string => {
  // Remove any spaces or special characters
  const cleanMobile = mobile.replace(/\D/g, '');
  // If it starts with +91, remove it
  if (cleanMobile.startsWith('91') && cleanMobile.length === 12) {
    return cleanMobile.substring(2);
  }
  // If it's 10 digits, return as is
  if (cleanMobile.length === 10) {
    return cleanMobile;
  }
  // Return the cleaned number (will be validated separately)
  return cleanMobile;
};

export const formatMobileWithCountryCode = (mobile: string): string => {
  const cleanMobile = formatMobileNumber(mobile);
  return `+91 ${cleanMobile}`;
};

// Family Member Validation Schema
export const familyMemberSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  relationship: z.string().min(1, 'Relationship is required'),
  dateOfBirth: z.string().optional(),
  occupation: z.string().optional(),
  mobile: z.string()
    .min(1, 'Mobile number is required')
    .refine((mobile) => validateMobileNumber(mobile), {
      message: 'Mobile number must be exactly 10 digits'
    }),
  aadhaar: z.string()
    .min(1, 'Aadhaar number is required')
    .refine((aadhaar) => validateAadhaarNumber(aadhaar), {
      message: 'Aadhaar number must be exactly 12 digits'
    })
});

export type FamilyMemberFormData = z.infer<typeof familyMemberSchema>;

// Banking Information Validation Functions
export const validateIFSCCode = (ifsc: string): boolean => {
  // IFSC code format: 4 letters + 7 characters (letters/numbers)
  const cleanIFSC = ifsc.replace(/\s/g, '').toUpperCase();
  return /^[A-Z]{4}[0-9A-Z]{7}$/.test(cleanIFSC);
};

export const validateAccountNumber = (accountNumber: string): boolean => {
  // Account number should be 9-18 digits
  const cleanAccount = accountNumber.replace(/\D/g, '');
  return /^[0-9]{9,18}$/.test(cleanAccount);
};

export const validateBankCode = (bankCode: string): boolean => {
  // Bank code should be 3-6 alphanumeric characters
  const cleanCode = bankCode.replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{3,6}$/.test(cleanCode);
};

export const validateBranchCode = (branchCode: string): boolean => {
  // Branch code should be 3-6 alphanumeric characters
  const cleanCode = branchCode.replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{3,6}$/.test(cleanCode);
};

export const validateSwiftCode = (swiftCode: string): boolean => {
  // SWIFT code format: 8 or 11 characters (letters)
  const cleanSwift = swiftCode.replace(/\s/g, '').toUpperCase();
  return /^[A-Z]{8}([A-Z]{3})?$/.test(cleanSwift);
};

export const validateIBANCode = (iban: string): boolean => {
  // IBAN format: 2 letters + 2 digits + up to 30 alphanumeric characters
  const cleanIBAN = iban.replace(/\s/g, '').toUpperCase();
  return /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/.test(cleanIBAN);
};

export const validateAccountHolderName = (name: string): boolean => {
  // Account holder name should contain only letters, spaces, and common punctuation
  return /^[a-zA-Z\s\.\-\']+$/.test(name.trim()) && name.trim().length >= 2;
};

export const validateBankName = (bankName: string): boolean => {
  // Bank name should contain letters, spaces, and common punctuation
  return /^[a-zA-Z\s\.\-\&\(\)]+$/.test(bankName.trim()) && bankName.trim().length >= 2;
};

export const validateBankBranch = (branch: string): boolean => {
  // Bank branch should contain letters, numbers, spaces, and common punctuation
  return /^[a-zA-Z0-9\s\.\-\&\(\)]+$/.test(branch.trim()) && branch.trim().length >= 2;
};

// Banking Information Validation Schema
export const bankingInfoSchema = z.object({
  countryName: z.string()
    .min(1, 'Country name is required')
    .min(2, 'Country name must be at least 2 characters')
    .max(50, 'Country name must be less than 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Country name can only contain letters and spaces'),
  
  accountHolderName: z.string()
    .min(1, 'Account holder name is required')
    .min(2, 'Account holder name must be at least 2 characters')
    .max(100, 'Account holder name must be less than 100 characters')
    .refine((name) => validateAccountHolderName(name), {
      message: 'Account holder name can only contain letters, spaces, and common punctuation'
    }),
  
  bankName: z.string()
    .min(1, 'Bank name is required')
    .min(2, 'Bank name must be at least 2 characters')
    .max(100, 'Bank name must be less than 100 characters')
    .refine((name) => validateBankName(name), {
      message: 'Bank name can only contain letters, spaces, and common punctuation'
    }),
  
  bankBranch: z.string()
    .min(1, 'Bank branch is required')
    .min(2, 'Bank branch must be at least 2 characters')
    .max(100, 'Bank branch must be less than 100 characters')
    .refine((branch) => validateBankBranch(branch), {
      message: 'Bank branch can only contain letters, numbers, spaces, and common punctuation'
    }),
  
  accountNumber: z.string()
    .min(1, 'Account number is required')
    .refine((account) => validateAccountNumber(account), {
      message: 'Account number must be 9-18 digits'
    }),
  
  accountType: z.string()
    .min(1, 'Account type is required')
    .refine((type) => ['Savings', 'Current', 'Fixed Deposit', 'Recurring Deposit', 'Salary', 'NRI'].includes(type), {
      message: 'Invalid account type selected'
    }),
  
  attachments: z.array(z.string())
    .min(1, 'At least one attachment is required')
    .max(20, 'Maximum 20 attachments allowed'),
  
  ifscCode: z.string()
    .min(1, 'IFSC code is required')
    .refine((ifsc) => validateIFSCCode(ifsc), {
      message: 'IFSC code must be in format: 4 letters followed by 7 alphanumeric characters (e.g., SBIN0001234)'
    }),
  
  ibanCode: z.string()
    .optional()
    .refine((iban) => !iban || validateIBANCode(iban), {
      message: 'IBAN code must be in format: 2 letters + 2 digits + 4-30 alphanumeric characters'
    }),
  
  bankCode: z.string()
    .min(1, 'Bank code is required')
    .refine((code) => validateBankCode(code), {
      message: 'Bank code must be 3-6 alphanumeric characters'
    }),
  
  branchCode: z.string()
    .optional()
    .refine((code) => !code || validateBranchCode(code), {
      message: 'Branch code must be 3-6 alphanumeric characters'
    }),
  
  swiftCode: z.string()
    .optional()
    .refine((code) => !code || validateSwiftCode(code), {
      message: 'SWIFT code must be 8 or 11 letters (e.g., SBININBB123)'
    }),
  
  status: z.string()
    .optional()
    .refine((status) => !status || ['Active', 'Inactive', 'Pending', 'Suspended'].includes(status), {
      message: 'Invalid status selected'
    }),
});

export type BankingInfoFormData = z.infer<typeof bankingInfoSchema>; 